import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import apiService from '../../services/ApiService';
import type { ExternalAwardInput, ExternalVehicleAward } from '../../types/judging';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';

interface Props {
  vehicleId: number;
  award?: ExternalVehicleAward;
  onClose: () => void;
  onSaved: (awards: ExternalVehicleAward[]) => void;
}

const suggestions = ['Best of Show', 'Best Mitsubishi', 'Best Engine Bay', 'Best Fitment'];

export default function ExternalAwardEditor({ vehicleId, award, onClose, onSaved }: Props) {
  const [input, setInput] = useState<ExternalAwardInput>({
    title: award?.title || '', event_title: award?.event_title || '',
    award_year: award?.award_year || new Date().getFullYear(),
    placement: award?.placement || '', location: award?.location || '', notes: award?.notes || '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirmRemoval, setConfirmRemoval] = useState(false);
  const change = (key: keyof ExternalAwardInput, value: string | number) => setInput(prev => ({ ...prev, [key]: value }));
  const save = async (remove = false) => {
    setBusy(true); setError('');
    try {
      const awards = remove && award
        ? await apiService.revokeExternalAward(vehicleId, award.id)
        : await apiService.saveExternalAward(vehicleId, input, award?.id);
      onSaved(awards);
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Unable to save award');
    } finally { setBusy(false); }
  };
  const fieldClass = 'input w-full';
  // Build sections create stacking contexts; mount above them so every action stays reachable.
  return createPortal(
    <Modal isOpen onClose={() => { if (!busy) onClose(); }} title={award ? 'Edit external award' : 'Add external award'}
      description="Record an award won outside Bilt:Spec. Saving publishes it as admin verified."
      mobileFullscreen closeOnEscape={!busy} closeOnOverlayClick={!busy}>
      <form onSubmit={event => { event.preventDefault(); void save(); }} className="space-y-4 min-h-0 flex-1 overflow-y-auto overscroll-contain">
        <fieldset disabled={busy} className="space-y-4">
          <label className="block text-sm text-gray-300">Award title
            <input className={fieldClass} value={input.title} maxLength={150} required list="external-award-titles"
              placeholder="Enter a custom award title" onChange={event => change('title', event.target.value)} />
          </label>
          <datalist id="external-award-titles">{suggestions.map(title => <option key={title} value={title}>{title}</option>)}</datalist>
          <p className="text-xs text-gray-500">Use any title, or choose a suggestion.</p>
          <label className="block text-sm text-gray-300">Event name
            <input className={fieldClass} value={input.event_title} maxLength={150} required
              onChange={event => change('event_title', event.target.value)} />
          </label>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <label className="block text-sm text-gray-300">Year won
              <input className={fieldClass} type="number" min={1900} max={new Date().getFullYear()} required
                value={input.award_year || ''} onChange={event => change('award_year', Number(event.target.value))} />
            </label>
            <label className="block text-sm text-gray-300">Placement (optional)
              <input className={fieldClass} value={input.placement} maxLength={50} placeholder="1st place"
                onChange={event => change('placement', event.target.value)} />
            </label>
          </div>
          <label className="block text-sm text-gray-300">Location (optional)
            <input className={fieldClass} value={input.location} maxLength={150}
              onChange={event => change('location', event.target.value)} />
          </label>
          <label className="block text-sm text-gray-300">Public notes (optional)
            <textarea className={fieldClass} value={input.notes} maxLength={1000} rows={3}
              onChange={event => change('notes', event.target.value)} />
          </label>
          {error ? <p role="alert" className="text-sm text-red-300">{error}</p> : null}
          {confirmRemoval ? (
            <div className="space-y-2">
              <p className="text-sm text-gray-300">Remove this award from the build page?</p>
              <Button type="button" variant="danger" onClick={() => void save(true)}>Confirm removal</Button>
              <Button type="button" variant="ghost" onClick={() => setConfirmRemoval(false)}>Keep award</Button>
            </div>
          ) : null}
          <div className="flex flex-wrap items-center gap-2">
            <Button type="submit">{busy ? 'Saving…' : award ? 'Save changes' : 'Verify and publish'}</Button>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            {award && !confirmRemoval ? <Button type="button" variant="ghost" onClick={() => setConfirmRemoval(true)}>Remove award</Button> : null}
          </div>
        </fieldset>
      </form>
    </Modal>, document.body
  );
}
